/**
 * Backend Service - Handles all API calls to the User Backend
 * 
 * Three-Layer Architecture:
 * Frontend → User Backend → Admin Backend
 * 
 * No API keys in frontend - uses session-based authentication
 */

// Configuration - Read from URL params (passed from dashboard)
const urlParams = new URLSearchParams(window.location.search);
const URL_API_URL = urlParams.get('api_url');
const URL_USER_EMAIL = urlParams.get('user_email');

// CRITICAL: User Backend URL - always use app.loopai.in for project operations
const USER_BACKEND_URL = 'https://app.loopai.in';

// Fallback API URL for local development only
const FALLBACK_API_URL = URL_API_URL || 'https://studio-pro-redesign.preview.emergentagent.com';

export const CONFIG = {
    // User Backend URL - the only backend this frontend talks to
    apiUrl: USER_BACKEND_URL,
    userEmail: URL_USER_EMAIL
};

console.log('Backend Service Configured:', {
    apiUrl: CONFIG.apiUrl,
    userEmail: CONFIG.userEmail
});

// Helper to fetch image/video via proxy to avoid CORS issues with CDN URLs (e.g., fal.media, GCS)
// Includes retry logic for transient failures
async function fetchImageViaProxy(imageUrl, retries = 2) {
    // Validate URL before making request
    if (!imageUrl || 
        typeof imageUrl !== 'string' || 
        imageUrl === 'undefined' || 
        imageUrl === 'null' ||
        imageUrl === '' ||
        imageUrl.trim() === '') {
        console.warn('⚠️ Skipping invalid image URL:', imageUrl);
        return null;
    }
    
    // If already a data URI, return as-is
    if (imageUrl.startsWith('data:')) {
        return imageUrl;
    }
    
    // Skip blob URLs - they can't be proxied
    if (imageUrl.startsWith('blob:')) {
        return imageUrl;
    }
    
    // Validate it's a proper HTTP(S) URL
    if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
        console.warn('⚠️ Skipping non-HTTP URL:', imageUrl.substring(0, 50));
        return null;
    }
    
    // Additional validation - check URL is parseable
    try {
        new URL(imageUrl);
    } catch (e) {
        console.warn('⚠️ Skipping malformed URL:', imageUrl.substring(0, 50));
        return null;
    }
    
    // ALWAYS use USER_BACKEND_URL for proxy operations
    const effectiveApiUrl = USER_BACKEND_URL;
    
    let lastError = null;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            if (attempt > 0) {
                console.log(`🔄 Retry ${attempt}/${retries} for:`, imageUrl.substring(0, 80) + '...');
                // Wait before retry (exponential backoff)
                await new Promise(resolve => setTimeout(resolve, 500 * attempt));
            } else {
                console.log('🔄 Fetching via proxy:', imageUrl.substring(0, 100) + '...');
            }
            
            const response = await fetch(`${effectiveApiUrl}/api/proxy/fetch-image`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image_url: imageUrl, return_base64: true, include_prefix: false })
            });
            
            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Unknown error');
                throw new Error(`Proxy fetch failed: ${response.status} ${response.statusText} - ${errorText.substring(0, 200)}`);
            }
            
            const data = await response.json();
            
            // Use base64_raw if available (no prefix), otherwise use base64
            const base64Data = data.base64_raw || data.base64;
            
            if (base64Data) {
                const mimeType = data.mime_type || data.content_type || 'image/png';
                console.log('✅ Proxy fetch successful, MIME type:', mimeType);
                
                // Check if prefix already exists - don't add it again
                if (base64Data.startsWith('data:')) {
                    console.log('✅ Base64 already has prefix');
                    return base64Data;
                }
                
                // Add prefix only if not present
                return `data:${mimeType};base64,${base64Data}`;
            } else if (data.error) {
                throw new Error(data.error);
            }
            
            // Check for alternative response formats
            if (data.data) {
                const mimeType = data.mime_type || data.content_type || 'image/png';
                return `data:${mimeType};base64,${data.data}`;
            }
            
            console.warn('Proxy response structure:', Object.keys(data));
            throw new Error('No base64 data in proxy response');
            
        } catch (e) {
            lastError = e;
            if (attempt < retries) {
                console.warn(`⚠️ Attempt ${attempt + 1} failed, will retry:`, e.message.substring(0, 100));
            }
        }
    }
    
    console.error('❌ Proxy fetch failed after', retries + 1, 'attempts for', imageUrl.substring(0, 80), ':', lastError?.message);
    return null;
}

// Export the proxy fetch function for use in other services
export { fetchImageViaProxy };

// Utility function to convert multiple image URLs to base64 via proxy
// Use this immediately after image generation to avoid CORS issues with expiring CDN URLs
export async function convertImagesToBase64(imageUrls) {
    if (!imageUrls || !Array.isArray(imageUrls)) return [];
    
    const results = await Promise.all(
        imageUrls.map(async (url) => {
            if (!url) return null;
            if (url.startsWith('data:')) return url; // Already base64
            
            try {
                const base64 = await fetchImageViaProxy(url);
                return base64;
            } catch (e) {
                console.error('Failed to convert image:', url.substring(0, 50), e.message);
                return url; // Return original URL as fallback
            }
        })
    );
    
    return results.filter(Boolean);
}

// Helper to convert data URI or Blob URL to Blob using fetch
async function dataURItoBlob(dataURI) {
    if (!dataURI || typeof dataURI !== 'string') return null;
    if (!dataURI.startsWith('data:') && !dataURI.startsWith('blob:')) return null;
    
    try {
        const res = await fetch(dataURI);
        return await res.blob();
    } catch (e) {
        console.error("Error converting URI to blob:", e);
        return null;
    }
}

// Helper to convert URL back to Base64 (Hydration) with MIME type correction
// Uses proxy for external URLs to avoid CORS issues
async function urlToBase64(url) {
    // Validate URL before processing
    if (!url || 
        typeof url !== 'string' || 
        url === 'undefined' || 
        url === 'null' ||
        url === '' ||
        url.trim() === '') {
        console.warn('⚠️ Skipping invalid URL in urlToBase64:', url);
        return null;
    }
    
    // Handle Google Storage URLs
    let fetchUrl = url;
    if (url.startsWith('gs://')) {
        fetchUrl = url.replace('gs://', 'https://storage.googleapis.com/');
    }

    // If not an HTTP URL, return as-is (could be a local path or data URI)
    if (!fetchUrl.startsWith('http')) {
        // Check if it's a data URI
        if (url.startsWith('data:')) return url;
        // Otherwise skip it
        console.warn('⚠️ Skipping non-HTTP URL:', url.substring(0, 50));
        return null;
    }

    try {
        // Use proxy endpoint to avoid CORS issues with external CDNs (fal.media, etc.)
        const proxyResult = await fetchImageViaProxy(fetchUrl);
        if (proxyResult && proxyResult.startsWith('data:')) {
            return proxyResult;
        }
        
        // Proxy failed - try direct fetch as fallback (may work for some URLs)
        console.warn('⚠️ Proxy returned null, attempting direct fetch for:', fetchUrl.substring(0, 80));
        try {
            let response = await fetch(fetchUrl, { mode: 'cors' });

            if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
            
            const blob = await response.blob();
            
            let finalBlob = blob;
            if (!blob.type || blob.type === 'application/octet-stream') {
                const lowerUrl = fetchUrl.toLowerCase();
                if (lowerUrl.endsWith('.png') || lowerUrl.includes('png')) finalBlob = new Blob([blob], { type: 'image/png' });
                else if (lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.jpeg') || lowerUrl.includes('jpeg') || lowerUrl.includes('jpg')) finalBlob = new Blob([blob], { type: 'image/jpeg' });
                else if (lowerUrl.endsWith('.webm') || lowerUrl.includes('webm')) finalBlob = new Blob([blob], { type: 'video/webm' });
                else if (lowerUrl.endsWith('.mp4') || lowerUrl.includes('mp4')) finalBlob = new Blob([blob], { type: 'video/mp4' });
                else if (lowerUrl.endsWith('.mp3') || lowerUrl.includes('mp3')) finalBlob = new Blob([blob], { type: 'audio/mpeg' });
                else {
                    finalBlob = new Blob([blob], { type: 'image/png' });
                }
            }

            return await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(finalBlob);
            });
        } catch (directFetchError) {
            console.warn('⚠️ Direct fetch also failed (CORS), keeping original URL:', directFetchError.message);
            // Return original URL - browser may be able to display it directly in <img> tag
            return fetchUrl;
        }
    } catch (e) {
        console.warn(`Failed to hydrate asset from ${url}, keeping URL. Error:`, e);
        return url; 
    }
}

// Recursive hydration for the project object
// For signed URLs: Just validate and pass through - no conversion needed
// Only convert to base64 when needed for canvas operations
async function hydrateProject(project) {
    console.log("🔄 Hydrating project - validating URLs...");
    const hydrated = JSON.parse(JSON.stringify(project));

    // Helper to validate URL - signed URLs from backend work directly
    const isValidMediaUrl = (url) => {
        if (!url || typeof url !== 'string') return false;
        if (url === 'undefined' || url === 'null' || url === '') return false;
        if (url.trim() === '') return false;
        
        // Valid data URI (base64)
        if (url.startsWith('data:') && url.length > 100 && url.includes('base64,')) {
            return true;
        }
        
        // Truncated/invalid data URI - skip
        if (url.startsWith('data:')) {
            console.warn('⚠️ Detected truncated/invalid data URI:', url.substring(0, 50));
            return false;
        }
        
        // Valid signed URL from backend (http/https)
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return true;
        }
        
        // GCS URL
        if (url.startsWith('gs://')) {
            return true;
        }
        
        return false;
    };

    // Simply validate and pass through - no proxy conversion needed for display
    const validateUrl = (url, context = '') => {
        if (!url) return null;
        
        if (!isValidMediaUrl(url)) {
            console.warn(`⚠️ Skipping invalid URL for ${context}:`, String(url).substring(0, 80));
            return null;
        }
        
        // Convert gs:// to https:// for direct access
        if (url.startsWith('gs://')) {
            const httpsUrl = url.replace('gs://', 'https://storage.googleapis.com/');
            console.log(`✅ Converted GCS URL for ${context}`);
            return httpsUrl;
        }
        
        // Signed URLs work directly - just pass through
        if (url.startsWith('http')) {
            console.log(`✅ Valid signed URL for ${context}:`, url.substring(0, 60) + '...');
        }
        
        return url;
    };

    // 1. Characters - validate URLs, don't convert
    if (hydrated.characters && Array.isArray(hydrated.characters)) {
        for (const char of hydrated.characters) {
            if (char.image) char.image = validateUrl(char.image, `character "${char.name}" image`);
            if (char.audio?.data) char.audio.data = validateUrl(char.audio.data, `character "${char.name}" audio`);
            if (char.referenceImages && Array.isArray(char.referenceImages)) {
                char.referenceImages = char.referenceImages.map((img, i) => validateUrl(img, `character "${char.name}" ref image ${i}`)).filter(Boolean);
            }
            if (char.costumeAndAccessoryImages && Array.isArray(char.costumeAndAccessoryImages)) {
                char.costumeAndAccessoryImages = char.costumeAndAccessoryImages.map((img, i) => validateUrl(img, `character "${char.name}" costume image ${i}`)).filter(Boolean);
            }
        }
    }

    // 2. Scenes (Shots) - validate URLs, don't convert
    if (hydrated.scenes && Array.isArray(hydrated.scenes)) {
        for (let sceneIdx = 0; sceneIdx < hydrated.scenes.length; sceneIdx++) {
            const scene = hydrated.scenes[sceneIdx];
            if (scene.shots && Array.isArray(scene.shots)) {
                for (let shotIdx = 0; shotIdx < scene.shots.length; shotIdx++) {
                    const shot = scene.shots[shotIdx];
                    if (shot.startFrameImage) shot.startFrameImage = validateUrl(shot.startFrameImage, `scene ${sceneIdx + 1} shot ${shotIdx + 1} start frame`);
                    if (shot.endFrameImage) shot.endFrameImage = validateUrl(shot.endFrameImage, `scene ${sceneIdx + 1} shot ${shotIdx + 1} end frame`);
                    if (shot.video) shot.video = validateUrl(shot.video, `scene ${sceneIdx + 1} shot ${shotIdx + 1} video`);
                }
            }
        }
    }

    // 3. Assets - validate URLs, don't convert
    if (hydrated.assets && Array.isArray(hydrated.assets)) {
        for (const asset of hydrated.assets) {
            if (asset.data) asset.data = validateUrl(asset.data, `asset ${asset.name || 'unnamed'}`);
        }
    }

    console.log("✅ Project hydration complete - URLs validated and ready for display.");
    return hydrated;
}

// Helper function for canvas operations ONLY - use when you need base64 for image editing
export async function fetchImageAsBase64ForCanvas(imageUrl) {
    if (!imageUrl || typeof imageUrl !== 'string') return null;
    
    // Already base64
    if (imageUrl.startsWith('data:')) return imageUrl;
    
    // Try loading directly with crossOrigin first (preferred)
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                canvas.getContext('2d').drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => reject(new Error('Direct load failed'));
            img.src = imageUrl;
        });
    } catch (directError) {
        console.warn('Direct canvas load failed, trying proxy:', directError.message);
        
        // Fallback to proxy
        return await fetchImageViaProxy(imageUrl);
    }
}

// Function to upload media files to GCS
async function uploadMediaToGCS(userEmail, apiUrl, mediaFiles) {
    console.log(`🚀 Uploading ${mediaFiles.length} media files to GCS...`);
    
    if (mediaFiles.length === 0) return {};

    const formData = new FormData();
    formData.append('user_email', userEmail);
    
    mediaFiles.forEach(file => {
        formData.append('files', file.blob, file.filename);
    });
    
    try {
        // Get API key and user email from sessionStorage
        const apiKey = sessionStorage.getItem('apiKey');
        const userEmail = sessionStorage.getItem('userEmail');
        
        const headers = {};
        if (apiKey) {
            headers['X-API-Key'] = apiKey;
        }
        if (userEmail) {
            headers['X-User-Email'] = userEmail;
        }
        
        const response = await fetch(`${apiUrl}/api/webhook/upload-media`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: formData
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Upload API returned error: ${response.status} ${errorText}`);
        }
        
        const result = await response.json();
        
        const urlMap = {};
        const errors = [];

        if (result.files && Array.isArray(result.files)) {
            result.files.forEach((file) => {
                if (!file.error && (file.gcs_url || file.public_url || file.url)) {
                    // Prefer public_url/url over gcs_url; convert gs:// to https:// if needed
                    let url = file.public_url || file.url || file.gcs_url;
                    if (url.startsWith('gs://')) {
                        const path = url.slice(5); // remove 'gs://'
                        url = `https://storage.googleapis.com/${path}`;
                        console.log('🔄 Converted gs:// to public URL:', url.substring(0, 80));
                    }
                    urlMap[file.filename] = url;
                } else if (file.error) {
                    const errorMsg = `Server reported error uploading ${file.filename}: ${typeof file.error === 'object' ? JSON.stringify(file.error) : file.error}`;
                    console.error(errorMsg);
                    errors.push(errorMsg);
                }
            });
        }
        
        if (errors.length > 0 && Object.keys(urlMap).length === 0) {
            throw new Error(errors[0]);
        }
        
        return urlMap;
    } catch (e) {
        console.error("Error in uploadMediaToGCS:", e);
        throw e;
    }
}

export const isPublicHttpsUrl = (url) => {
    if (typeof url !== 'string' || !/^https:\/\//i.test(url)) return false;
    try {
        const host = new URL(url).hostname;
        return !/^(localhost|127\.|0\.0\.0\.0|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host) && host !== '[::1]';
    } catch { return false; }
};

/**
 * Returns a public https URL for a video — uploads blob:/data:/local videos to storage first.
 */
export async function ensurePublicVideoUrl(url, onProgress) {
    if (!url) throw new Error('No video to process.');
    if (isPublicHttpsUrl(url)) return url;
    if (onProgress) onProgress('Uploading video to storage…');
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Could not read the local video (${res.status}).`);
    const blob = await res.blob();
    const type = blob.type && blob.type.startsWith('video/') ? blob.type : 'video/mp4';
    const ext = (type.split('/')[1] || 'mp4').replace('quicktime', 'mov');
    const file = new File([blob], `editor_video_${Date.now()}.${ext}`, { type });
    return uploadFileToBackend(file);
}

/**
 * Uploads a File/Blob (audio, video, image) to the backend and returns a public URL
 */
export async function uploadFileToBackend(file) {
    const userEmail = sessionStorage.getItem('userEmail');
    const ext = (file.name && file.name.includes('.')) ? file.name.split('.').pop() : (file.type.split('/')[1] || 'bin');
    const filename = `ref_upload_${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
    const urlMap = await uploadMediaToGCS(userEmail, CONFIG.apiUrl, [{ blob: file, filename }]);
    const url = urlMap[filename] || (Object.values(urlMap).length === 1 ? Object.values(urlMap)[0] : null);
    if (!url) throw new Error('Upload succeeded but no URL was returned.');
    return url;
}

/**
 * Uploads a Data URI to the backend and returns a public URL
 */
export async function uploadDataUriToBackend(dataUri, userEmail) {
    const blob = await dataURItoBlob(dataUri);
    if (!blob) throw new Error("Invalid data URI or empty content for upload");
    
    const ext = blob.type.split('/')[1]?.split(';')[0] || 'bin';
    const filename = `temp_upload_${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
    
    console.log(`Preparing to upload ${filename} (${blob.size} bytes, ${blob.type})`);
    
    const urlMap = await uploadMediaToGCS(userEmail, CONFIG.apiUrl, [{ blob, filename }]);
    
    if (urlMap[filename]) {
        return urlMap[filename];
    }
    
    const returnedUrls = Object.values(urlMap);
    if (returnedUrls.length === 1) {
        console.warn(`Filename mismatch in upload response. Requested: ${filename}, using returned URL: ${returnedUrls[0]}`);
        return returnedUrls[0];
    }

    console.error("Upload response map:", JSON.stringify(urlMap, null, 2));
    throw new Error(`Upload processed but no valid URL returned for the file ${filename}.`);
}

export async function saveImagesToBackend(imageUrls, userEmail, projectName) {
    // Filter out undefined/null values first
    const validUrls = (imageUrls || []).filter(url => url && typeof url === 'string');
    
    if (validUrls.length === 0) {
        console.warn("saveImagesToBackend: No valid image URLs provided");
        return { success: true, urls: [] };
    }
    
    // ALWAYS use USER_BACKEND_URL for save operations
    const effectiveApiUrl = USER_BACKEND_URL;
    
    const base64Images = await Promise.all(
        validUrls.map(async (imageUrl) => {
            try {
                // If already a data URI, extract base64 part
                if (imageUrl.startsWith('data:')) return imageUrl.split(',')[1];
                
                // For HTTP URLs, use proxy to avoid CORS issues
                if (imageUrl.startsWith('http')) {
                    // Use proxy endpoint to fetch image
                    const proxyResult = await fetchImageViaProxy(imageUrl);
                    if (proxyResult && proxyResult.startsWith('data:')) {
                        return proxyResult.split(',')[1];
                    }
                    
                    // Fallback: try direct fetch (may fail for CDNs like fal.media)
                    console.warn('Proxy failed for saveImages, trying direct fetch:', imageUrl.substring(0, 50));
                    try {
                        const response = await fetch(imageUrl);
                        const blob = await response.blob();
                        return new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                const res = reader.result;
                                resolve(res.split(',')[1]);
                            };
                            reader.onerror = () => resolve("");
                            reader.readAsDataURL(blob);
                        });
                    } catch (directFetchError) {
                        console.error('Direct fetch also failed:', directFetchError.message);
                        return "";
                    }
                }
                
                // If it's already a base64 string without prefix
                if (imageUrl.length > 100 && !imageUrl.includes('/')) {
                    return imageUrl;
                }
                console.warn("Unknown image URL format:", imageUrl.substring(0, 50));
                return "";
            } catch (e) {
                console.error("Failed to convert image to base64:", e);
                return "";
            }
        })
    );
    
    const validImages = base64Images.filter(img => img && img.length > 0);
    
    if (validImages.length === 0) {
        console.warn("saveImagesToBackend: No valid images after conversion");
        return { success: true, urls: [] };
    }

    try {
        const apiKey = sessionStorage.getItem('apiKey');
        const storedUserEmail = sessionStorage.getItem('userEmail');
        
        const headers = { 'Content-Type': 'application/json' };
        if (apiKey) {
            headers['X-API-Key'] = apiKey;
        }
        if (storedUserEmail) {
            headers['X-User-Email'] = storedUserEmail;
        }
        
        const response = await fetch(`${effectiveApiUrl}/api/webhook/save-images`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({ user_email: userEmail, project_name: projectName, images: validImages })
        });
        
        if (!response.ok) throw new Error(`Failed to save images: ${response.statusText}`);
        return await response.json();
    } catch (error) {
        console.error("Error saving images to backend:", error);
        return { success: false, error: error.message };
    }
}

export async function deductCredits(userEmail, creditsUsed, projectId, projectName) {
    // Credits are now managed server-side automatically
    // This function is kept for backward compatibility but does nothing
    console.log(`Credits deduction requested: ${creditsUsed} (managed server-side)`);
    return { success: true, message: 'Credits managed server-side' };
}

// Cloud Rendering Functions
export const startCloudRender = async (projectData, userEmail) => {
    console.log('🎬 Starting video render...');
    const projectId = projectData.projectId || projectData.id || 'temp-' + Date.now();
    
    const apiKey = sessionStorage.getItem('apiKey');
    const storedUserEmail = sessionStorage.getItem('userEmail');
    // ALWAYS use USER_BACKEND_URL for render operations
    const effectiveApiUrl = USER_BACKEND_URL;
    
    const headers = {
        'Content-Type': 'application/json'
    };
    
    if (apiKey) {
        headers['X-API-Key'] = apiKey;
    }
    if (storedUserEmail) {
        headers['X-User-Email'] = storedUserEmail;
    }
    
    console.log('🎬 Sending render request to:', `${effectiveApiUrl}/api/webhook/render-video`);

    // Build a clean, explicit overlays payload so the renderer doesn't need to
    // traverse timeline.clips[] or guess field names. Every field is provided
    // (with defaults) so the backend has a consistent contract.
    const rawOverlayClips = (projectData.timeline?.clips || []).filter(
        c => c.type === 'text' || c.type === 'logo' || c.type === 'image'
    );

    const normalizedOverlays = rawOverlayClips.map(c => {
        const start = Number(c.start) || 0;
        const duration = Number(c.duration) || 5;
        const common = {
            id: c.id,
            type: c.type,
            start_time: start,
            end_time: start + duration,
            duration,
            // Position: percentages (0-100), treated as CENTER of overlay
            x: c.x ?? 50,
            y: c.y ?? 50,
            // Size: percentages (0-100) of frame
            width: c.width ?? (c.type === 'logo' ? 15 : 40),
            height: c.height ?? (c.type === 'logo' ? 15 : 40),
            rotation: c.rotation ?? 0,
            opacity: c.opacity ?? 1,
            z_index: c.zIndex ?? (c.type === 'logo' ? 10 : c.type === 'text' ? 20 : 5),
            // Transition: { type: 'fade-in'|'fade-out'|'cross-dissolve'|'none', duration: seconds }.
            // Used by the renderer to animate logo cross-dissolves and outro
            // fade-outs. Backend should ignore unknown types and treat
            // missing/null as 'none'.
            transition: c.transition || { type: 'none', duration: 0 },
            // Mark whether the clip was auto-injected by the Branding step,
            // so the renderer can apply branding-specific defaults if needed.
            branding_role: c.brandingRole || null
        };
        if (c.type === 'text') {
            return {
                ...common,
                content: c.content || '',
                font_size: c.fontSize ?? 12,      // % of frame height
                color: c.color || '#ffffff',
                font_family: c.fontFamily || 'sans-serif',
                font_weight: c.fontWeight || 'normal',
                text_align: c.textAlign || 'center',
                background_color: c.backgroundColor || null,
                stroke_color: c.strokeColor || null,
                stroke_width: c.strokeWidth ?? 0
            };
        }
        // logo / image
        return {
            ...common,
            src: c.src || null,                // GCS URL after upload
            asset_id: c.assetId || null,       // fallback lookup into project_data.assets[]
            name: c.name || null
        };
    });

    if (normalizedOverlays.length > 0) {
        console.log(`🎨 Sending ${normalizedOverlays.length} overlay clip(s) to renderer (every field shown explicitly):`);
        normalizedOverlays.forEach((o, idx) => {
            console.log(`  [${idx + 1}/${normalizedOverlays.length}] ${o.type.toUpperCase()}`, JSON.stringify(o, null, 2));
        });
    } else {
        console.log('🎨 No overlay clips in timeline');
    }

    // Build a normalized audio_tracks array (music + SFX). Clips live on timeline.clips
    // with type='audio' and are differentiated by trackId (track-audio-music / track-audio-sfx).
    const tracksById = Object.fromEntries(
        (projectData.timeline?.tracks || []).map(t => [t.id, t])
    );
    const assetsById = Object.fromEntries(
        (projectData.assets || []).map(a => [a.id, a])
    );

    const classifyAudioKind = (clip) => {
        const track = tracksById[clip.trackId];
        const trackName = (track?.name || '').toLowerCase();
        if (trackName.includes('sfx')) return 'sfx';
        if (trackName.includes('music')) return 'music';
        // Fallback: guess from trackId string
        if ((clip.trackId || '').toLowerCase().includes('sfx')) return 'sfx';
        return 'music';
    };

    const normalizedAudioTracks = (projectData.timeline?.clips || [])
        .filter(c => c.type === 'audio')
        .map(c => {
            const start = Number(c.start) || 0;
            const duration = Number(c.duration) || 0;
            const asset = assetsById[c.assetId];
            // asset.data was already swapped to a GCS URL by the save pipeline
            const src = (asset && typeof asset.data === 'string' && !asset.data.startsWith('data:'))
                ? asset.data
                : null;
            return {
                id: c.id,
                kind: classifyAudioKind(c),             // 'music' or 'sfx'
                track_id: c.trackId || null,
                start_time: start,
                end_time: start + duration,
                duration,
                src,                                     // GCS URL (null if upload failed)
                asset_id: c.assetId || null,            // fallback lookup into project_data.assets[]
                name: c.name || null,
                volume: c.volume ?? 1,                  // 0-1
                fade_in: c.fadeIn ?? 0,                 // seconds (0 if unused)
                fade_out: c.fadeOut ?? 0,
                loop: c.loop ?? false
            };
        });

    if (normalizedAudioTracks.length > 0) {
        console.log(`🔊 Sending ${normalizedAudioTracks.length} audio track(s) to renderer (every field shown explicitly):`);
        normalizedAudioTracks.forEach((a, idx) => {
            console.log(`  [${idx + 1}/${normalizedAudioTracks.length}] ${a.kind.toUpperCase()}`, JSON.stringify(a, null, 2));
        });
    } else {
        console.log('🔊 No music/SFX clips in timeline');
    }

    // Inject structured arrays into project_data so backend has a single source of truth
    // Explicit mix contract: original video audio must be MIXED with audio_tracks, never replaced.
    const audioMix = { keep_original_audio: true, original_audio_volume: 1 };
    const enrichedProjectData = {
        ...projectData,
        overlays: normalizedOverlays,
        audio_tracks: normalizedAudioTracks,
        ...audioMix
    };
    
    // Step 1: Make the fetch request
    let response;
    try {
        response = await fetch(`${effectiveApiUrl}/api/webhook/render-video`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({
                user_email: userEmail,
                project_id: projectId,
                project_data: enrichedProjectData,
                overlays: normalizedOverlays,      // top-level for convenience
                audio_tracks: normalizedAudioTracks, // top-level for convenience
                ...audioMix
            })
        });
    } catch (networkError) {
        // Network-level failure (no response at all)
        console.error('🎬 Network error:', networkError);
        throw new Error(`Network error: ${networkError.message}`);
    }

    console.log('🎬 Render response status:', response.status);

    // Step 2: Read body ONCE as text. Clone first so third-party interceptors
    // (e.g. preview-runtime) can't cause "body stream already read" crashes.
    let responseText = '';
    try {
        const cloned = response.clone();
        responseText = await cloned.text();
        console.log('🎬 Render response body:', responseText.substring(0, 500));
    } catch (e) {
        // Clone failed — try the original stream; if that also fails, fall back to empty body
        try {
            responseText = await response.text();
        } catch (e2) {
            console.warn('🎬 Could not read response body:', e2.message);
            responseText = '';
        }
    }

    // Step 3: Check if response is OK
    if (!response.ok) {
        // 404, 500, etc.
        let errorDetail = responseText || `HTTP ${response.status}`;
        try {
            if (responseText) {
                const errorJson = JSON.parse(responseText);
                errorDetail = errorJson.detail || errorJson.error || errorJson.message || responseText;
            }
        } catch (e) {
            // Keep original text if JSON parsing fails
        }
        // Make 404 explicit so users know it's a backend route issue
        if (response.status === 404) {
            throw new Error(`Render endpoint not found (404). The admin backend route /api/webhook/render-video appears to be unavailable. Please contact your admin team to verify the endpoint is deployed.`);
        }
        throw new Error(`Server error ${response.status}: ${errorDetail}`);
    }

    // Step 4: Parse the text we already read as JSON
    let result;
    try {
        result = JSON.parse(responseText);
    } catch (e) {
        throw new Error(`Invalid JSON response: ${responseText.slice(0, 200)}`);
    }
    
    console.log(`✅ Render job started: ${result.job_id}`);
    return { success: true, jobId: result.job_id };
};

export const checkCloudRenderStatus = async (jobId, userEmail) => {
    const apiKey = sessionStorage.getItem('apiKey');
    const storedUserEmail = sessionStorage.getItem('userEmail') || userEmail;
    // ALWAYS use USER_BACKEND_URL for render status
    const effectiveApiUrl = USER_BACKEND_URL;
    
    const headers = {
        'Content-Type': 'application/json'
    };
    
    if (apiKey) {
        headers['X-API-Key'] = apiKey;
    }
    if (storedUserEmail) {
        headers['X-User-Email'] = storedUserEmail;
    }
    
    // Step 1: Make the fetch request
    let response;
    try {
        response = await fetch(
            `${effectiveApiUrl}/api/webhook/render-video-status?job_id=${jobId}&user_email=${encodeURIComponent(storedUserEmail)}`,
            {
                headers,
                credentials: 'include'
            }
        );
    } catch (networkError) {
        throw new Error(`Network error: ${networkError.message}`);
    }

    // Step 2: Read body ONCE as text (with clone to survive external interceptors)
    let responseText = '';
    try {
        const cloned = response.clone();
        responseText = await cloned.text();
    } catch (e) {
        try {
            responseText = await response.text();
        } catch (e2) {
            console.warn('Could not read status response body:', e2.message);
            responseText = '';
        }
    }

    // Step 3: Check if response is OK
    if (!response.ok) {
        throw new Error(`Status check failed: ${response.status} - ${responseText}`);
    }

    // Step 4: Parse JSON from the text
    try {
        return JSON.parse(responseText);
    } catch (e) {
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
    }
};

export const pollRenderUntilComplete = async (
    jobId,
    userEmail,
    onProgress,
    maxWaitSeconds = 300
) => {
    const startTime = Date.now();
    const pollInterval = 10000; // Poll every 10 seconds
    
    console.log(`🔄 Starting to poll render status for job: ${jobId}`);
    
    while (true) {
        const elapsedSeconds = (Date.now() - startTime) / 1000;
        
        if (elapsedSeconds > maxWaitSeconds) {
            return { success: false, error: 'Render timed out after 5 minutes' };
        }

        try {
            const status = await checkCloudRenderStatus(jobId, userEmail);
            
            console.log(`🔄 Poll status: ${status.status}`);
            
            if (onProgress) onProgress(status);

            if (status.status === 'completed' && status.url) {
                console.log('✅ Render completed!');
                return { success: true, url: status.url };
            }

            if (status.status === 'failed') {
                console.error(`❌ Render failed: ${status.error}`);
                return { success: false, error: status.error || 'Rendering failed' };
            }
            
            // 'queued' or 'processing' → keep polling
        } catch (error) {
            console.warn('Poll error (will retry):', error.message);
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
};

// Project Saving
//
// Per-project mutex (in-memory, module-scoped). Guarantees that at most ONE
// POST /api/video-projects/save is in flight per project at any moment.
//
// Why this exists: a brand-new project starts with `projectId=null`. The
// backend issues a stable UUID on first save and returns it as
// `result.project_id`. If two saves for the same logical project race —
// e.g. manual `handleSaveProject` + autosave both fire before the first
// response lands — both will POST with `projectId=null` and the backend
// will create TWO rows. Subsequent saves then ping-pong between them.
//
// Implementation: a Map<localId, Promise<chain>>. Each save chains on the
// previous save's promise, guaranteeing strict serialization per project.
// Keyed on the local `project.id` (uuid minted by handleCreateProject in
// App.js) which is stable across the project's lifetime regardless of
// whether the backend has assigned `projectId` yet.
const _saveInFlight = new Map(); // localId -> Promise<saveResult>

export const saveProjectToBackend = async (project, userEmail, apiUrl = null) => {
    const lockKey = project?.id || project?.projectId;
    if (!lockKey) {
        // No id at all — fall through unsynchronized. Should never happen
        // because handleCreateProject always assigns `id`.
        return _saveProjectToBackendImpl(project, userEmail, apiUrl);
    }

    const prior = _saveInFlight.get(lockKey);
    const chained = (async () => {
        if (prior) {
            console.log(`💾 Save already in flight for ${lockKey}, awaiting prior…`);
            const priorResult = await prior.catch(() => null);
            // Defensive writeback: if the prior save just minted a
            // backend project_id and our caller hasn't observed it yet
            // (because their setActiveProject is still pending), patch
            // it onto the incoming project object so this POST sends
            // the canonical id and the backend UPDATEs instead of
            // creating a duplicate row.
            if (priorResult?.project_id && !project.projectId) {
                project.projectId = priorResult.project_id;
            }
        }
        return _saveProjectToBackendImpl(project, userEmail, apiUrl);
    })();

    _saveInFlight.set(lockKey, chained);
    chained.finally(() => {
        // Only clear if still the same promise (newer chained save may
        // have replaced it).
        if (_saveInFlight.get(lockKey) === chained) {
            _saveInFlight.delete(lockKey);
        }
    });
    return chained;
};

const _saveProjectToBackendImpl = async (project, userEmail, apiUrl = null) => {
    console.log('💾 Starting optimized save...');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000);
    
    // ALWAYS use USER_BACKEND_URL for save operations
    const effectiveApiUrl = USER_BACKEND_URL;
    
    console.log('💾 Saving project to:', effectiveApiUrl);

    try {
        const mediaFiles = [];
        
        const getExt = (blob) => {
            const type = blob.type.split('/')[1]?.split(';')[0];
            return type || 'bin';
        };
        const isMedia = (str) => str && (str.startsWith('data:') || str.startsWith('blob:'));

        // 1. Characters
        for (const char of project.characters) {
            if (isMedia(char.image)) {
                const blob = await dataURItoBlob(char.image);
                if (blob) mediaFiles.push({ blob, filename: `char_${char.id}_image.${getExt(blob)}` });
            }
            if (isMedia(char.audio?.data)) {
                const blob = await dataURItoBlob(char.audio.data);
                if (blob) mediaFiles.push({ blob, filename: `char_${char.id}_audio.${getExt(blob)}` });
            }
            if (char.referenceImages) {
                for (let idx = 0; idx < char.referenceImages.length; idx++) {
                    const img = char.referenceImages[idx];
                    if (isMedia(img)) {
                        const blob = await dataURItoBlob(img);
                        if (blob) mediaFiles.push({ blob, filename: `char_${char.id}_ref_${idx}.${getExt(blob)}` });
                    }
                }
            }
            if (char.costumeAndAccessoryImages) {
                for (let idx = 0; idx < char.costumeAndAccessoryImages.length; idx++) {
                    const img = char.costumeAndAccessoryImages[idx];
                    if (isMedia(img)) {
                        const blob = await dataURItoBlob(img);
                        if (blob) mediaFiles.push({ blob, filename: `char_${char.id}_costume_${idx}.${getExt(blob)}` });
                    }
                }
            }
        }

        // 2. Scenes
        for (const scene of project.scenes) {
            for (const shot of scene.shots) {
                if (isMedia(shot.startFrameImage)) {
                    const blob = await dataURItoBlob(shot.startFrameImage);
                    if (blob) mediaFiles.push({ blob, filename: `shot_${shot.id}_start.${getExt(blob)}` });
                }
                if (isMedia(shot.endFrameImage)) {
                    const blob = await dataURItoBlob(shot.endFrameImage);
                    if (blob) mediaFiles.push({ blob, filename: `shot_${shot.id}_end.${getExt(blob)}` });
                }
                if (isMedia(shot.video)) {
                    const blob = await dataURItoBlob(shot.video);
                    if (blob) mediaFiles.push({ blob, filename: `shot_${shot.id}_video.${getExt(blob)}` });
                }
            }
        }

        // 3. Assets — defensive: legacy / brand-picker flows sometimes stashed
        // an object under `project.assets` instead of the canonical flat
        // array. Silently coerce to `[]` so the save doesn't die with
        // "e.assets is not iterable". The stashed object contained no
        // `.data` fields anyway, so nothing to upload from it.
        if (Array.isArray(project.assets)) {
            for (const asset of project.assets) {
                if (isMedia(asset.data)) {
                    const blob = await dataURItoBlob(asset.data);
                    if (blob) mediaFiles.push({ blob, filename: `asset_${asset.id}.${getExt(blob)}` });
                }
            }
        } else if (project.assets) {
            console.warn('⚠️  project.assets is not an array — coercing to [] before save', typeof project.assets);
            project.assets = [];
        }

        console.log(`📦 Found ${mediaFiles.length} media files to upload`);

        let urlMap = {};
        if (mediaFiles.length > 0) {
            urlMap = await uploadMediaToGCS(userEmail, effectiveApiUrl, mediaFiles);
            console.log(`✅ Media upload phase complete. Mapped ${Object.keys(urlMap).length} files.`);
        }

        const optimizedProject = JSON.parse(JSON.stringify(project));

        const exactNameMap = {};
        Object.keys(urlMap).forEach(key => {
            const lastDotIndex = key.lastIndexOf('.');
            if (lastDotIndex !== -1) {
                const nameWithoutExt = key.substring(0, lastDotIndex);
                exactNameMap[nameWithoutExt] = urlMap[key];
            }
        });

        const findUrl = (name) => exactNameMap[name];

        // Replace blobs with URLs
        optimizedProject.characters.forEach((char) => {
            if (isMedia(char.image)) {
                const url = findUrl(`char_${char.id}_image`);
                if (url) char.image = url;
            }
            if (isMedia(char.audio?.data)) {
                const url = findUrl(`char_${char.id}_audio`);
                if (url) char.audio.data = url;
            }
            if (char.referenceImages) {
                char.referenceImages = char.referenceImages.map((img, idx) => {
                    if (isMedia(img)) return findUrl(`char_${char.id}_ref_${idx}`) || img;
                    return img;
                });
            }
            if (char.costumeAndAccessoryImages) {
                char.costumeAndAccessoryImages = char.costumeAndAccessoryImages.map((img, idx) => {
                    if (isMedia(img)) return findUrl(`char_${char.id}_costume_${idx}`) || img;
                    return img;
                });
            }
        });

        optimizedProject.scenes.forEach((scene) => {
            scene.shots.forEach((shot) => {
                if (isMedia(shot.startFrameImage)) {
                    const url = findUrl(`shot_${shot.id}_start`);
                    if (url) shot.startFrameImage = url;
                }
                if (isMedia(shot.endFrameImage)) {
                    const url = findUrl(`shot_${shot.id}_end`);
                    if (url) shot.endFrameImage = url;
                }
                if (isMedia(shot.video)) {
                    const url = findUrl(`shot_${shot.id}_video`);
                    if (url) shot.video = url;
                }
            });
        });

        optimizedProject.assets.forEach((asset) => {
            if (isMedia(asset.data)) {
                const url = findUrl(`asset_${asset.id}`);
                if (url) asset.data = url;
            }
        });

        // Sync timeline-clip src (logo/image overlays) with the uploaded asset URLs.
        // Clip.src is a duplicate base64 of asset.data from Studio.handleImageUpload —
        // without this the render backend receives a massive inline payload and often
        // fails to composite the overlay.
        if (optimizedProject.timeline?.clips) {
            optimizedProject.timeline.clips.forEach((clip) => {
                if ((clip.type === 'logo' || clip.type === 'image') && isMedia(clip.src)) {
                    const asset = optimizedProject.assets.find(a => a.id === clip.assetId);
                    if (asset && !isMedia(asset.data)) {
                        // asset.data now holds the GCS URL
                        clip.src = asset.data;
                    }
                }
            });
        }

        console.log('💾 Saving project metadata to canonical video-projects endpoint...');

        // NEW (Apr 2026 migration): switched from POST /api/webhook/save-project (legacy)
        // to POST /api/video-projects/save (canonical db.video_projects collection).
        // Cookie-based auth — no API key headers required.
        //
        // Top-level metadata: the DB schema stores `rendered_video_url`, `status`,
        // etc. as TOP-LEVEL columns on the video_projects row (not nested under
        // project_data). The dashboard reads these top-level fields to decide
        // whether a project is "Completed" + show "Download Rendered Video".
        // We must therefore mirror the frontend's `renderedVideoUrl` + render
        // metadata up to the top of the save payload — otherwise saving a
        // freshly-rendered project from AutoPilot/RenderModal leaves the
        // dashboard stuck on "In Progress" forever.
        const payload = {
            project_id: project.projectId || null,    // null on first save → backend issues a uuid
            name: project.name,
            project_type: 'studio_pro',
            project_data: optimizedProject
        };
        // Read render metadata from the canonical (camelCase) frontend fields,
        // falling back to snake_case in case a backend GET response was passed
        // straight back to save without normalisation.
        const renderedVideoUrl = project.renderedVideoUrl || project.rendered_video_url;
        const renderedVideoGcsUri = project.renderedVideoGcsUri || project.rendered_video_gcs_uri;
        if (renderedVideoUrl) payload.rendered_video_url = renderedVideoUrl;
        if (renderedVideoGcsUri) payload.rendered_video_gcs_uri = renderedVideoGcsUri;
        // If the project carries a rendered video, the dashboard should show
        // it as Completed. We don't override an explicit status the project
        // may already carry (e.g. 'failed').
        if (renderedVideoUrl && !project.status) payload.status = 'completed';
        else if (project.status) payload.status = project.status;

        const saveResponse = await fetch(`${effectiveApiUrl}/api/video-projects/save`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!saveResponse.ok) {
            // ── 410 Gone: project was deleted (tombstoned) on the backend ──
            // User backend now rejects saves for tombstoned project_ids.
            // Surface this distinctly so App.js can drop the IDB row + clear
            // the editor instead of retrying in a loop.
            if (saveResponse.status === 410) {
                const errorText = await saveResponse.text().catch(() => '');
                let detail = 'Project was deleted from your account';
                try {
                    const parsed = JSON.parse(errorText);
                    detail = parsed.detail || parsed.error || detail;
                } catch { /* noop */ }
                console.warn(`⚠️  Project ${project.projectId} is tombstoned (410): ${detail}`);
                clearTimeout(timeoutId);
                return {
                    success: false,
                    gone: true,
                    project_id: project.projectId,
                    error: detail
                };
            }

            const errorText = await saveResponse.text();
            throw new Error(`Server returned ${saveResponse.status}: ${errorText}`);
        }

        const result = await saveResponse.json();
        console.log(`✅ Project saved successfully. ID: ${result.project_id}`);
        
        return { success: true, project_id: result.project_id, optimizedProject };

    } catch (error) {
        clearTimeout(timeoutId);
        
        const isNetworkError = error.message === 'Failed to fetch' || error.message.includes('NetworkError');
        
        if (isNetworkError) {
             console.warn("Backend save skipped (offline/network error):", error.message);
        } else {
             console.error("Backend save failed:", error);
        }
        
        let errorMessage = error.message;
        if (error.name === 'AbortError') {
            errorMessage = "Request timed out after 5 minutes.";
        }
        
        return { success: false, error: errorMessage };
    }
};

// Get Projects
// ============================================
// HELPER: detect URLs already persisted to GCS
// ============================================
// Per Movie Studio Project Source Migration spec (Apr 2026): admin backend now persists
// CDN URLs to GCS server-side. When a URL is already on storage.googleapis.com (or gs://),
// callers can skip the proxy-fetch + base64 + re-upload roundtrip.
export const isAlreadyPersisted = (url) =>
    typeof url === 'string' &&
    (url.includes('storage.googleapis.com') || url.startsWith('gs://'));

export const getProjectsFromBackend = async (userEmail, apiUrl = null) => {
    try {
        // ALWAYS use USER_BACKEND_URL for project operations
        const effectiveApiUrl = USER_BACKEND_URL;
        console.log('📂 Fetching projects from canonical video-projects endpoint:', effectiveApiUrl);

        // NEW: switched from POST /api/webhook/list-projects (legacy db.projects collection,
        // ~1066 stub bloat) to GET /api/video-projects (canonical db.video_projects).
        // ?project_type=studio_pro excludes Ad Creator and Advanced Studio rows.
        const response = await fetch(
            `${effectiveApiUrl}/api/video-projects?project_type=studio_pro`,
            {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            }
        );

        const responseText = await response.text();
        if (!response.ok) {
            if (response.status === 404 || response.status === 401) {
                return { success: true, projects: [] };
            }
            throw new Error(`Failed to fetch projects: ${response.status} ${responseText || response.statusText}`);
        }

        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
        }

        // The endpoint may return either {projects: [...]} or [...] directly
        const rawProjects = Array.isArray(result) ? result : (result.projects || []);
        console.log(`📂 Found ${rawProjects.length} studio_pro projects`);

        // Map canonical schema → frontend shape used by ProjectDashboard
        const projects = rawProjects.map((p) => ({
            id: p.id,
            name: p.name,
            // last_modified is epoch ms; existing UI expects `lastModified` camelCase
            lastModified: p.last_modified || p.lastModified || 0,
            projectId: p.id,                                  // canonical id (was project_id on legacy)
            creditsUsed: p.credits_used || p.creditsUsed || 0,
            // Pass through new fields so the UI can show richer metadata
            status: p.status,
            project_type: p.project_type,
            rendered_video_url: p.rendered_video_url,
            rendered_video_gcs_uri: p.rendered_video_gcs_uri,
            image_count: p.image_count,
            series_id: p.series_id,
            episode_number: p.episode_number,
            created_at: p.created_at
        }))
        // Newest first
        .sort((a, b) => (b.lastModified || 0) - (a.lastModified || 0));

        return { success: true, projects };
    } catch (error) {
        const isNetworkError = error.message === 'Failed to fetch' || error.message.includes('NetworkError');
        if (isNetworkError) {
            console.warn("Backend unavailable (offline mode):", error.message);
        } else {
            console.error("Backend fetch failed:", error);
        }
        return { success: false, projects: [], error: error.message };
    }
};

/**
 * One-shot sync of locally-cached IndexedDB projects to the canonical backend.
 * @param {Array<{ project_data: object, project_type?: string }>} projects
 *        Max 200 per call, ~15 MB per project (server enforces limits).
 * @returns {{ success: boolean, summary?: {...}, results?: [...], error?: string }}
 */
export const bulkSaveProjects = async (projects) => {
    if (!Array.isArray(projects) || projects.length === 0) {
        return { success: true, summary: { total: 0, created: 0, updated: 0, failed: 0 }, results: [] };
    }
    try {
        const effectiveApiUrl = USER_BACKEND_URL;
        const response = await fetch(`${effectiveApiUrl}/api/video-projects/bulk-save`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ projects })
        });
        const text = await response.text();
        if (!response.ok) {
            return { success: false, error: `HTTP ${response.status}: ${text.substring(0, 200)}` };
        }
        try {
            return JSON.parse(text);
        } catch {
            return { success: false, error: `Invalid JSON: ${text.substring(0, 200)}` };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// Load Project
// Apr 2026 migration: switched from POST /api/webhook/get-project (legacy webhook with
// X-API-Key auth and user_email body footgun) to GET /api/video-projects/{id} (RESTful,
// cookie auth, scoped to the logged-in session user).
export const loadProjectFromBackend = async (projectId, userEmail, apiUrl = null) => {
    try {
        const effectiveApiUrl = USER_BACKEND_URL;
        console.log(`📂 Loading project ${projectId} from canonical video-projects endpoint`);

        const response = await fetch(
            `${effectiveApiUrl}/api/video-projects/${encodeURIComponent(projectId)}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            }
        );

        if (!response.ok) {
            // 404 covers both "missing" and "belongs to another user" (no info leak)
            if (response.status === 404) {
                console.warn(`Project ${projectId} not found on backend. Falling back to local.`);
                return { success: false, error: 'Project not found on server' };
            }
            const errorText = await response.text();
            throw new Error(`Failed to load project: ${response.status} ${errorText || response.statusText}`);
        }

        const result = await response.json();

        // Canonical response is a flat object with project_data nested.
        // Merge top-level metadata (id, name, last_modified, rendered_video_url, etc.)
        // INTO project_data so existing client code (which consumes a single project object)
        // keeps working without a refactor.
        const inner = result.project_data || {};
        const merged = {
            ...inner,
            // Authoritative server metadata wins over any stale fields inside project_data
            id: inner.id || result.id,
            projectId: result.id,
            name: result.name || inner.name,
            lastModified: result.last_modified || inner.lastModified,
            createdAt: result.created_at || inner.createdAt,
            status: result.status,
            project_type: result.project_type,
            renderedVideoUrl: result.rendered_video_url,
            renderedVideoGcsUri: result.rendered_video_gcs_uri,
            ffmpegRenderStatus: result.ffmpeg_render_status,
            renderCompletedAt: result.render_completed_at,
            seriesId: result.series_id,
            episodeNumber: result.episode_number,
            imageCount: result.image_count
        };

        console.log('📥 Received project from backend:', {
            projectId: merged.projectId,
            name: merged.name,
            charactersCount: merged.characters?.length,
            scenesCount: merged.scenes?.length,
            sampleCharImage: merged.characters?.[0]?.image?.substring(0, 100),
            sampleShotImage: merged.scenes?.[0]?.shots?.[0]?.startFrameImage?.substring(0, 100)
        });

        const hydratedProject = await hydrateProject(merged);
        return { success: true, project: hydratedProject };
    } catch (error) {
        if (error.message === 'Project not found on server') {
            return { success: false, error: error.message };
        }
        const isNetworkError = error.message === 'Failed to fetch' || error.message.includes('NetworkError');
        if (isNetworkError) {
            console.warn("Backend load skipped (offline mode):", error.message);
        } else {
            console.error("Backend load failed:", error);
        }
        return { success: false, error: error.message };
    }
};
